import { FC, useRef, useState } from "react";
import { Box, Paper, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, IconButton, Tooltip, Autocomplete, TextField } from "@material-ui/core";
import { Cancel, Edit, Save } from "@material-ui/icons";
import { Formik } from "formik";
import Scrollbar from "src/components/shared/Scrollbar";
import InputField from "src/components/ui-components/InputField";

type CurrencyTableColumnProps = {
  width?: number | string;
  headerName: string;
  field: string;
  editable: boolean;
  rowKey?: string | number;
  align?: 'inherit' | 'left' | 'center' | 'right' | 'justify';
  inputType?: "select" | "text";
  options?: any[];
  dataValueField?: string | null;
  dataTextField?: string | null;
  handleChange?: any;
  listKey?: string;
  required?: boolean;
};

type CurrencyTableHeadProps = {
  columns: CurrencyTableColumnProps[];
};

type CurrencyTableRowProps = {
  columns: CurrencyTableColumnProps[];
  row: any;
  rowKey: number | string;
  isEditing: boolean;
  key: number | string | null;
  initialForm: any;
  onEdit: () => void;
  onSave: (values: any) => void;
  handleCancel: () => void;
  formValidationSchema: any;
  formRef?: any;
};

type CurrencyTableProps = {
  columns: CurrencyTableColumnProps[];
  dataList: any[];
  isLoading: boolean;
  onClickEdit: any;
  handleSave: any;
  initialForm: any;
  editingRow: number | null;
  handleCancel: any;
  formValidationSchema: any;
  formRef?: any;
};

type CurrencyTableCellProps = {
  key: number | string;
  column: CurrencyTableColumnProps;
  value: any;
  rowKey: number | string;
  idx: number | string;
  editable: boolean;
  isEditing: boolean;
  formRef?: any;
  setFieldTouched?: any;
};

const CurrencyTableHead = (props: CurrencyTableHeadProps) => {
  const { columns } = props;
  return (
    <TableHead>
      <TableRow style={{ position: "sticky", top: 0, zIndex: 1 }}>
        {columns.map((headCell, i) => (
          <TableCell
            width={headCell.width}
            key={i.toString()}
            align={headCell.align}
          >
            {headCell.headerName}
          </TableCell>
        ))}
        <TableCell width='150px' align="center">Actions</TableCell>
      </TableRow>
    </TableHead>
  );
};

const CurrencyTableRow: FC<CurrencyTableRowProps> = (props) => {
  const { row, columns, rowKey, isEditing, onEdit, onSave, handleCancel, formValidationSchema, formRef } = props;
  const [validateOnMount, setValidateOnMount] = useState(false);

  const handleSave = async () => {
    await formRef.current.validateForm();
    if (!formRef.current.isValid) {
      setValidateOnMount(true);
      return;
    }
    setValidateOnMount(false);
    onSave(formRef.current.values);
  };

  const getColumns = () => {
    if (isEditing) {
      return (
        <Formik
          initialValues={row}
          onSubmit={(values) => onSave(values)}
          innerRef={formRef}
          validationSchema={formValidationSchema}
          validateOnMount={validateOnMount}
        >
          {({ values, setFieldTouched }) => (
            <>
              {columns.map((column, idx) => (
                <CurrencyTableCell
                  key={idx}
                  column={column}
                  value={values[column.field]}
                  rowKey={rowKey}
                  idx={idx}
                  editable={column.editable}
                  isEditing={isEditing}
                  formRef={formRef}
                  setFieldTouched={setFieldTouched}
                />
              ))}
              <TableCell sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1 }} >
                <IconButton
                  onClick={handleCancel}
                  sx={{
                    height: 77,
                  }}
                >
                  <Cancel />
                </IconButton>
                <IconButton
                  onClick={handleSave}
                  sx={{
                    height: 77,
                  }}
                >
                  <Save />
                </IconButton>
              </TableCell>
            </>
          )}
        </Formik>
      );
    } else {
      return (
        <>
          {columns.map((column, idx) => (
            <CurrencyTableCell
              key={idx}
              column={column}
              value={row[column.field]}
              rowKey={rowKey}
              idx={idx}
              editable={column.editable}
              isEditing={false}
            />
          ))}
          <TableCell sx={{ display: 'flex', justifyContent: 'center', borderBottom: '1px solid rgba(224, 224, 224, 1)' }}>
            <IconButton onClick={onEdit}>
              <Edit />
            </IconButton>
          </TableCell>
        </>
      );
    }
  };

  return (
    <TableRow hover role="checkbox" tabIndex={-1} key={rowKey}>
      {getColumns()}
    </TableRow>
  );
};

const CurrencyTableCell: FC<CurrencyTableCellProps> = (props) => {
  const { column, rowKey, idx, value, editable, isEditing, formRef, setFieldTouched } = props;

  const getFieldInput = () => {
    const error = Boolean(formRef?.current?.errors[column.field]);
    const helperText = formRef?.current?.errors[column.field];

    if (column.inputType === 'select') {
      if (isEditing && column.editable) {
        return (
          <Autocomplete
            noOptionsText="No Data Available"
            value={column.options.find((x) => x[column.dataValueField] === value) || null}
            onChange={async (event: any, newValue) => {
              if (column.handleChange) {
                column.handleChange(newValue);
              } else if (newValue !== null) {
                await formRef.current.setFieldValue(column.field, newValue[column.dataValueField]);
              } else {
                formRef.current.setFieldValue(column.field, null);
              }
              setFieldTouched(column.field, true);
              event.stopPropagation();
            }}
            disableClearable={column.required}
            options={column.options}
            size="small"
            getOptionLabel={(option) => option[column.dataTextField]?.toString() || ""}
            getOptionSelected={(option, value) =>
              option[column.dataValueField] === value[column.dataValueField]
            }
            renderOption={(props, option, { selected }) => (
              <li {...props} key={option[column.listKey]}>
                <span>{option[column.dataTextField]}</span>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={`${column.headerName} ${column.required ? '*' : ''}`}
                error={error}
                helperText={helperText || " "}
                size="small"
                FormHelperTextProps={{
                  sx: { margin: 0, lineHeight: 2 },
                }}
                inputProps={{
                  ...params.inputProps,
                  autoComplete: "off",
                }}
              />
            )}
          />
        );
      } else {
        return (
          <Tooltip title={column?.options?.find(x => x[column.dataValueField] === value)?.[column.dataTextField]?.toString() || value}>
            <div>{column?.options?.find(x => x[column.dataValueField] === value)?.[column.dataTextField]?.toString() || value}</div>
          </Tooltip>
        )
      }
    } else if (column.inputType === 'text' && column.editable) {
      if (isEditing) {
        return (
          <InputField
            name={column.field}
            label={column.headerName}
            value={value}
            sx={{ pb:3 }}
            size="small"
            InputLabelProps={{
              shrink: !!value,
            }}
          />
        );
      } else {
        return (
          <Tooltip title={value}>
            <div>{value}</div>
          </Tooltip>
        )
      }
    } else {
      return (
        <Tooltip title={value}>
          <div>{value}</div>
        </Tooltip>
      );
    }
  };

  return (
    <TableCell
      align={column.align || "center"}
      key={`${rowKey}-${idx}`}
      sx={{ pb:0 }}
      style={{
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        maxWidth: column.width,
        cursor: editable ? "pointer" : "default"
      }}
    >
      {getFieldInput()}
    </TableCell>
  );
};

const CurrencyTable: FC<CurrencyTableProps> = (props) => {
  const { isLoading, columns, dataList, initialForm, handleSave, editingRow, handleCancel, formValidationSchema, formRef, onClickEdit } = props;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const tableRef = useRef<HTMLTableElement>(null);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleEdit = (rowIdx: number) => {
    onClickEdit(rowIdx, page, rowsPerPage);
  };

  const handleSaveRow = (rowIdx: number, values: any) => {
    handleSave(rowIdx, values);
  };

  return (
    <>
      <Box>
        <TableContainer component={Paper} style={{ maxHeight: "65vh", minHeight: "30vh" }}>
          {isLoading ? (
            <>
              <Skeleton sx={{ ml: 2, mr: 2, height: 70 }} />
              {Array.from(Array(5).keys()).map((index) => (
                <Skeleton
                  key={index}
                  sx={{ ml: 2, mr: 2, mt: -2, height: 70 }}
                />
              ))}
            </>
          ) : (
            <>
              <Scrollbar>
                <Table
                  aria-label="collapsible table"
                  sx={{ minWidth: "max-content" }}
                  ref={tableRef} stickyHeader
                >
                  <CurrencyTableHead columns={columns} />
                  <TableBody>
                    {dataList.slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage
                    )
                      .map((row, idx) => (
                        <CurrencyTableRow
                          key={idx}
                          rowKey={row.id}
                          row={row}
                          columns={columns}
                          isEditing={idx === editingRow}
                          initialForm={initialForm}
                          onEdit={() => handleEdit(idx)}
                          onSave={(values) => handleSaveRow(idx, values)}
                          handleCancel={handleCancel}
                          formValidationSchema={formValidationSchema}
                          formRef={formRef}
                        />
                      ))}
                  </TableBody>
                </Table>
              </Scrollbar>
            </>
          )}
        </TableContainer>
      </Box>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={dataList.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </>
  );
};

export default CurrencyTable;
